import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Divider,
  ListItemText,
  ListItemIcon,
  Typography,
  Button as MuiButton,
  Avatar,
  Tooltip,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  AppBar,
  TitlePortal,
  usePermissions,
  useRedirect,
  useLocaleState,
  useGetIdentity,
  useAuthProvider,
  LoadingIndicator,
  useTranslate,
} from 'react-admin';
import { useLocation } from 'react-router-dom';
import { fetchPendingReviewSummary } from '../../services/pendingEventsService';
import {
  APPLICATION_LIST_PATHS,
  APPLICATION_TYPE_LABELS,
  fetchPendingApplicationsSummary,
} from '../../services/pendingApplicationsService';
import { subscribePendingApplicationsChanged } from '../../services/pendingApplicationsEvents';
import { getEventActionUrl } from '../../utils/eventListUtils';
import {
  buildNotifFingerprint,
  countUnreadNotifs,
  setSeenNotifFingerprint,
} from '../../utils/panelNotifSeen';

const BRAND = '#AE256D';

const roleLabel = (crole) => {
  const map = {
    admin: 'Admin',
    operator: 'Operatör',
    editor: 'Editör',
    venueManager: 'Mekan Yöneticisi',
    organisationCompanyManager: 'Org. Yöneticisi',
    affiliate: 'Affiliate',
  };
  return map[crole] || crole || 'Kullanıcı';
};

const LanguageSwitcher = () => {
  const [locale, setLocale] = useLocaleState();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (newLocale) => {
    setLocale(newLocale);
    handleClose();
  };

  const currentText = locale === 'en' ? 'EN' : 'TR';

  return (
    <>
      <Tooltip title="Dil">
        <Button
          color="inherit"
          onClick={handleOpen}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13,
            minWidth: 0,
            px: 1.25,
            py: 0.5,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.35)',
            color: '#fff',
          }}
        >
          {currentText}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem selected={locale === 'tr'} onClick={() => handleSelect('tr')}>
          Türkçe
        </MenuItem>
        <MenuItem selected={locale === 'en'} onClick={() => handleSelect('en')}>
          English
        </MenuItem>
      </Menu>
    </>
  );
};

const PanelUserMenu = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const authProvider = useAuthProvider();
  const redirect = useRedirect();
  const [anchorEl, setAnchorEl] = useState(null);

  const email = permissions?.email || '';
  const name =
    [permissions?.name, permissions?.surname].filter(Boolean).join(' ').trim() ||
    permissions?.fullName ||
    identity?.fullName ||
    email ||
    'User';
  const initial = String(name).charAt(0).toLocaleUpperCase('tr-TR') || 'U';
  const open = Boolean(anchorEl);

  const handleLogout = async () => {
    setAnchorEl(null);
    await authProvider.logout();
    redirect('/login');
  };

  return (
    <>
      <Tooltip title={email || name}>
        <Button
          className="gise-user-menu"
          color="inherit"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18, opacity: 0.85 }} />}
          sx={{
            textTransform: 'none',
            color: '#fff',
            ml: 0.5,
            pl: 0.75,
            pr: 1,
            py: 0.5,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.35)',
            gap: 0.75,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: 'rgba(255,255,255,0.22)',
              color: '#fff',
            }}
          >
            {initial}
          </Avatar>
          <Box
            sx={{
              display: { xs: 'none', md: 'block' },
              textAlign: 'left',
              lineHeight: 1.15,
              maxWidth: 160,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>
              {name}
            </Typography>
          </Box>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 260,
            borderRadius: 2,
            boxShadow: '0 8px 28px rgba(15,23,42,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.75, bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                fontWeight: 700,
                bgcolor: BRAND,
                color: '#fff',
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                {name}
              </Typography>
              {email ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                  noWrap
                >
                  {email}
                </Typography>
              ) : null}
              <Box
                sx={{
                  mt: 0.75,
                  display: 'inline-block',
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  bgcolor: 'rgba(174,37,109,0.1)',
                  color: BRAND,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {roleLabel(permissions?.crole)}
              </Box>
            </Box>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ py: 1.25 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.auth.logout', { _: 'Çıkış Yap' })}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const getForcedAppBarTitle = (pathname, translate) => {
  if (pathname === '/events-v2/create') {
    return translate('menu.new_event', { _: 'Yeni Etkinlik' });
  }
  if (pathname === '/events-v2' || pathname.startsWith('/events-v2/')) {
    return translate('menu.upcoming_events', { _: 'Yaklaşan Etkinlikler' });
  }
  return '';
};

const CustomAppBar = () => {
  const { permissions } = usePermissions();
  const redirect = useRedirect();
  const translate = useTranslate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [eventTotal, setEventTotal] = useState(0);
  const [eventItems, setEventItems] = useState([]);
  const [applicationTotal, setApplicationTotal] = useState(0);
  const [applicationItems, setApplicationItems] = useState([]);
  const [applicationByType, setApplicationByType] = useState({
    venue: 0,
    organisationCompany: 0,
    salesAgent: 0,
  });
  const [seenTick, setSeenTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const [events, applications] = await Promise.all([
        fetchPendingReviewSummary(),
        fetchPendingApplicationsSummary(),
      ]);

      setEventTotal(events.total || 0);
      setEventItems(events.items || []);
      setApplicationTotal(applications.total || 0);
      setApplicationItems(applications.items || []);
      setApplicationByType(
        applications.byType || {
          venue: 0,
          organisationCompany: 0,
          salesAgent: 0,
        },
      );
    } catch {
      setEventTotal(0);
      setEventItems([]);
      setApplicationTotal(0);
      setApplicationItems([]);
      setApplicationByType({
        venue: 0,
        organisationCompany: 0,
        salesAgent: 0,
      });
    }
  }, []);

  useEffect(() => {
    if (!permissions) return undefined;
    load();
    const t = setInterval(load, 60000);
    const unsubscribe = subscribePendingApplicationsChanged(load);
    return () => {
      clearInterval(t);
      unsubscribe();
    };
  }, [load, permissions]);

  const open = Boolean(anchorEl);
  const rawTotal = eventTotal + applicationTotal;
  const fingerprint = useMemo(
    () => buildNotifFingerprint({ eventItems, applicationItems }),
    [eventItems, applicationItems],
  );
  const badgeTotal = useMemo(
    () => countUnreadNotifs(rawTotal, fingerprint),
    [rawTotal, fingerprint, seenTick],
  );

  const handleMarkAllRead = () => {
    setSeenNotifFingerprint(fingerprint);
    setSeenTick((n) => n + 1);
  };

  const showNotifs = Boolean(permissions);
  const forcedTitle = useMemo(
    () => getForcedAppBarTitle(location.pathname || '', translate),
    [location.pathname, translate],
  );

  return (
    <AppBar
      alwaysOn
      color="secondary"
      userMenu={<PanelUserMenu />}
      toolbar={<LoadingIndicator />}
      sx={{
        bgcolor: `${BRAND} !important`,
        backgroundColor: `${BRAND} !important`,
        backgroundImage: 'none !important',
        color: '#fff',
        '& .MuiToolbar-root': {
          color: '#fff',
          minHeight: 56,
          gap: 0.5,
        },
        '& .MuiIconButton-root': { color: '#fff' },
        // Logo en sola (hamburger’den önce)
        '& .RaAppBar-toolbar': { display: 'flex' },
        '& .RaAppBar-menuButton': { order: 1 },
        '& .gise-appbar-logo': { order: 0 },
        '& .gise-appbar-title': { order: 2, flex: 1, minWidth: 0 },
        '& .gise-appbar-tools': { order: 3 },
        '& .RaLoadingIndicator-root': { order: 4 },
        '& .RaUserMenu-root, & .gise-user-menu': { order: 5 },
      }}
    >
      <Box
        className="gise-appbar-logo"
        component="a"
        href="#/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          ml: 0.5,
          mr: 0.5,
          textDecoration: 'none',
          lineHeight: 0,
        }}
      >
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/brand/gise-kibris-white.svg`}
          alt="Gişe Kıbrıs"
          sx={{
            height: { xs: 22, sm: 26 },
            width: 'auto',
            maxWidth: { xs: 120, sm: 168 },
            display: 'block',
          }}
        />
      </Box>

      {forcedTitle ? (
        <Typography
          className="gise-appbar-title"
          sx={{
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 16,
            fontWeight: 600,
            color: '#fff',
          }}
        >
          {forcedTitle}
        </Typography>
      ) : (
        <TitlePortal className="gise-appbar-title" />
      )}

      <Box
        className="gise-appbar-tools"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
          ml: 'auto',
        }}
      >
        <LanguageSwitcher />

        {showNotifs ? (
          <>
            <Tooltip title="Bildirimler">
              <IconButton
                color="inherit"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  load();
                }}
                aria-label="notifications"
                size="small"
                sx={{
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 2,
                  p: 0.75,
                }}
              >
                <Badge badgeContent={badgeTotal} color="error" max={99}>
                  <NotificationsIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { width: 380, maxHeight: 520, mt: 1 } }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2">Bildirimler ({rawTotal})</Typography>
                <MuiButton
                  size="small"
                  onClick={handleMarkAllRead}
                  disabled={rawTotal === 0 || badgeTotal === 0}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Tümünü okundu yap
                </MuiButton>
              </Box>
              <Divider />

              {permissions?.crole === 'admin' ? (
                <>
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Bekleyen etkinlikler ({eventTotal})
                    </Typography>
                  </Box>
                  {eventItems.length === 0 ? (
                    <MenuItem disabled>Bekleyen etkinlik yok</MenuItem>
                  ) : (
                    eventItems.slice(0, 5).map((ev) => (
                      <MenuItem
                        key={`ev-${ev.id}`}
                        onClick={() => {
                          setAnchorEl(null);
                          window.location.href = getEventActionUrl(
                            { id: ev.slug || ev.id, slug: ev.slug, uid: ev.id },
                            'show',
                          );
                        }}
                      >
                        <ListItemText
                          primary={ev.name}
                          secondary={ev.createdby || '—'}
                          primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                          secondaryTypographyProps={{ fontSize: 11 }}
                        />
                      </MenuItem>
                    ))
                  )}
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      redirect('/events-v2?view=pending');
                    }}
                  >
                    Bekleyen etkinlikleri gör
                  </MenuItem>
                  <Divider />
                </>
              ) : null}

              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Bekleyen başvurular ({applicationTotal})
                </Typography>
              </Box>
              {applicationTotal === 0 ? (
                <MenuItem disabled>Bekleyen başvuru yok</MenuItem>
              ) : (
                applicationItems.slice(0, 8).map((item) => (
                  <MenuItem
                    key={`app-${item.id}`}
                    onClick={() => {
                      setAnchorEl(null);
                      redirect(`/eventCreationApplications/${item.id}/show`);
                    }}
                  >
                    <ListItemText
                      primary={item.name || '—'}
                      secondary={`${
                        APPLICATION_TYPE_LABELS[item.companyType] || item.companyType
                      } · ${item.email || '—'}`}
                      primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                      secondaryTypographyProps={{ noWrap: true, fontSize: 11 }}
                    />
                  </MenuItem>
                ))
              )}

              {applicationByType.venue > 0 ? (
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    redirect(APPLICATION_LIST_PATHS.venue);
                  }}
                >
                  Mekan başvuruları ({applicationByType.venue})
                </MenuItem>
              ) : null}
              {applicationByType.organisationCompany > 0 ? (
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    redirect(APPLICATION_LIST_PATHS.organisationCompany);
                  }}
                >
                  Organizatör başvuruları ({applicationByType.organisationCompany})
                </MenuItem>
              ) : null}
              {applicationByType.salesAgent > 0 ? (
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    redirect(APPLICATION_LIST_PATHS.salesAgent);
                  }}
                >
                  Satış ortağı başvuruları ({applicationByType.salesAgent})
                </MenuItem>
              ) : null}
            </Menu>
          </>
        ) : null}
      </Box>
    </AppBar>
  );
};

export default CustomAppBar;
